import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

/**
 * Wraps a recharts `<ResponsiveContainer>` and delays its first mount
 * by one animation frame. Recharts emits noisy "width(-1) and height(-1)"
 * console warnings when the container is measured before its parent has
 * laid out (e.g. inside a `TabsContent` that's mid-mount, or a chart
 * card revealed by a state flip). One-frame defer is enough for the
 * parent's offsetWidth/offsetHeight to settle.
 */
export const DeferredChart = ({ children, height = 280, minHeight = 80 }) => {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  if (!ready) {
    return (
      <div
        style={{ minHeight, height }}
        aria-hidden="true"
        data-testid="chart-mount-placeholder"
      />
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
};

/**
 * Reusable Y-zoom trend chart. The 0-100% axis often makes lines that
 * cluster within a small band (e.g. Motivation drivers within ~5pp)
 * visually overlap; the zoom control tightens the Y-window so subtle
 * differences become readable.
 *
 * Modes (radio):
 *   • Auto-fit  — Y range fits the data with 3pp padding (default)
 *   • 0–100%    — full range
 *   • Custom    — dual range slider (1pp granularity, cross-clamp)
 *
 * Props:
 *   data            — array of period rows
 *   xKey            — period field on each row
 *   lines           — [{ dataKey, name, stroke, dashed?, dot? }, …]
 *   title, subtitle — card-header strings
 *   toolbar         — optional React node rendered above the chart
 *                     (e.g. journey-stage drill-down pills)
 *   testIdPrefix    — namespaces zoom-button data-testids
 *   height          — px (default 280)
 */
export const ZoomableTrendChart = ({
  data, xKey = 'period', lines, title, subtitle, toolbar,
  testIdPrefix = 'trend', height = 280,
}) => {
  const [zoomMode, setZoomMode] = React.useState('auto');
  const [customRange, setCustomRange] = React.useState([0, 100]);

  const autoRange = React.useMemo(() => {
    if (!data?.length || !lines?.length) return [0, 1];
    let lo = Infinity, hi = -Infinity;
    data.forEach((d) => {
      lines.forEach(({ dataKey }) => {
        const v = d[dataKey];
        if (typeof v === 'number') { lo = Math.min(lo, v); hi = Math.max(hi, v); }
      });
    });
    if (!isFinite(lo) || !isFinite(hi)) return [0, 1];
    const pad = 0.03;
    return [Math.max(0, lo - pad), Math.min(1, hi + pad)];
  }, [data, lines]);

  React.useEffect(() => {
    if (zoomMode === 'custom') {
      setCustomRange([
        Math.round(autoRange[0] * 100),
        Math.round(autoRange[1] * 100),
      ]);
    }
  }, [zoomMode, autoRange]);

  const yDomain = React.useMemo(() => {
    if (zoomMode === 'full') return [0, 1];
    if (zoomMode === 'custom') return [customRange[0] / 100, customRange[1] / 100];
    return autoRange;
  }, [zoomMode, customRange, autoRange]);

  const tickStep = React.useMemo(() => {
    const span = (yDomain[1] - yDomain[0]) * 100;
    if (span <= 8) return 1;
    if (span <= 20) return 2;
    if (span <= 40) return 5;
    return 10;
  }, [yDomain]);
  const ticks = React.useMemo(() => {
    const lo = Math.ceil(yDomain[0] * 100);
    const hi = Math.floor(yDomain[1] * 100);
    const out = [];
    for (let v = lo - (lo % tickStep); v <= hi; v += tickStep) {
      if (v >= lo) out.push(v / 100);
    }
    if (out[out.length - 1] !== yDomain[1]) out.push(yDomain[1]);
    return out;
  }, [yDomain, tickStep]);

  return (
    <Card data-testid={`${testIdPrefix}-chart`}>
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px]">
            {['auto', 'full', 'custom'].map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setZoomMode(mode)}
                data-testid={`${testIdPrefix}-zoom-${mode}`}
                className={`rounded px-2 py-0.5 capitalize transition-colors ${
                  zoomMode === mode
                    ? 'bg-white text-slate-800 shadow-sm font-semibold'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                title={
                  mode === 'auto' ? 'Auto-fit Y axis to data range (default)'
                  : mode === 'full' ? 'Lock Y axis to 0–100%'
                  : 'Custom Y range — drag the slider'
                }
              >
                {mode === 'auto' ? 'Auto-fit' : mode === 'full' ? '0–100%' : 'Custom'}
              </button>
            ))}
          </div>
          {zoomMode !== 'full' && (
            <span className="text-[11px] tabular-nums text-slate-500">
              {Math.round(yDomain[0] * 100)}–{Math.round(yDomain[1] * 100)}%
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {toolbar && <div className="pb-3">{toolbar}</div>}
        {zoomMode === 'custom' && (
          <div className="px-2 pb-3" data-testid={`${testIdPrefix}-zoom-slider`}>
            <input
              type="range" min={0} max={100} value={customRange[0]}
              onChange={(e) => {
                const lo = Math.min(Number(e.target.value), customRange[1] - 1);
                setCustomRange([lo, customRange[1]]);
              }}
              className="w-full accent-amber-500 mb-1"
              data-testid={`${testIdPrefix}-zoom-min`}
              aria-label="Y-axis minimum %"
            />
            <input
              type="range" min={0} max={100} value={customRange[1]}
              onChange={(e) => {
                const hi = Math.max(Number(e.target.value), customRange[0] + 1);
                setCustomRange([customRange[0], hi]);
              }}
              className="w-full accent-amber-500"
              data-testid={`${testIdPrefix}-zoom-max`}
              aria-label="Y-axis maximum %"
            />
          </div>
        )}
        <DeferredChart height={height}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
            <YAxis
              domain={yDomain}
              ticks={ticks}
              tickFormatter={(v) => `${(v * 100).toFixed(tickStep < 2 ? 1 : 0)}%`}
              tick={{ fontSize: 11 }}
              allowDataOverflow
            />
            <RechartsTooltip formatter={(v) => `${(v * 100).toFixed(1)}%`} />
            {lines.length > 1 && <Legend />}
            {lines.map((ln) => (
              <Line
                key={ln.dataKey}
                type="monotone"
                dataKey={ln.dataKey}
                name={ln.name}
                stroke={ln.stroke}
                strokeWidth={ln.strokeWidth ?? 2.5}
                strokeDasharray={ln.dashed ? '4 2' : undefined}
                dot={ln.dot ?? { r: 3 }}
              />
            ))}
          </LineChart>
        </DeferredChart>
      </CardContent>
    </Card>
  );
};
